import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Button, Form, Badge, Modal, Spinner, Alert, InputGroup } from 'react-bootstrap';
import adminService from '../../services/adminService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const ROLE_LABELS = {
  jr_inspector: 'Inspector',
  sr_inspector: 'Reviewer',
  admin: 'Admin',
};

const EMPTY_FORM = { emp_code: '', ename: '', email: '', phone: '', department: '', role: '' };

const ManageEmployeesSection = () => {
  const [employees, setEmployees] = useState([]);
  const [kpis, setKpis] = useState({ total_employees: 0, inspectors: 0, senior_inspectors: 0 });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null); // null = adding, object = editing
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [blockTarget, setBlockTarget] = useState(null);
  const [blockError, setBlockError] = useState('');
  const [blocking, setBlocking] = useState(false);

  const { page, setPage, totalPages, paginatedItems: paginatedEmployees } = usePagination(employees);

  const fetchEmployees = useCallback(async (search = '', department = '') => {
    setLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (department) params.department = department;
      const res = await adminService.getEmployees(params);
      if (res.success) {
        setEmployees(res.employees);
        setKpis(res.kpis);
        setDepartments(res.departments || []);
      } else {
        setLoadError(res.message || 'Failed to load employees');
      }
    } catch (err) {
      console.error('Failed to load employees:', err);
      setLoadError(err.message || 'Failed to reach the server. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEmployees(searchInput, departmentFilter);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setDepartmentFilter('');
    setPage(1);
    fetchEmployees('', '');
  };

  const openAddModal = () => {
    setEditingEmployee(null);
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowAddModal(true);
  };

  const openEditModal = (emp) => {
    setEditingEmployee(emp);
    setFormData({
      emp_code: emp.emp_code,
      ename: emp.ename || '',
      email: emp.email || '',
      phone: emp.phone || '',
      department: emp.department || '',
      role: emp.assigned_role || '',
    });
    setFormError('');
    setShowAddModal(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      if (editingEmployee) {
        const res = await adminService.updateEmployee(editingEmployee.eid, {
          ename: formData.ename,
          email: formData.email,
          phone: formData.phone,
          department: formData.department,
          role: formData.role,
        });
        if (!res.success) throw new Error(res.message);
      } else {
        const res = await adminService.createEmployee(formData);
        if (!res.success) throw new Error(res.message);
      }
      setShowAddModal(false);
      fetchEmployees(searchInput, departmentFilter);
    } catch (err) {
      setFormError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmBlockToggle = (emp) => {
    setBlockTarget(emp);
    setBlockError('');
  };

  const handleBlockToggle = async () => {
    if (!blockTarget) return;
    const nextBlocked = blockTarget.login_status !== 'blocked';
    setBlocking(true);
    setBlockError('');
    try {
      const res = await adminService.setEmployeeBlock(blockTarget.eid, nextBlocked);
      if (!res.success) throw new Error(res.message);
      setBlockTarget(null);
      fetchEmployees(searchInput, departmentFilter);
    } catch (err) {
      setBlockError(err.message || 'Failed to update employee status');
    } finally {
      setBlocking(false);
    }
  };

  return (
    <div>
      {loadError && (
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {loadError}
        </Alert>
      )}
      {/* KPIs */}
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card className="text-center shadow-sm h-100 border-primary">
            <Card.Body>
              <i className="fas fa-users text-primary mb-2" style={{ fontSize: '2rem' }}></i>
              <h3 className="fw-bold mb-0">{kpis.total_employees}</h3>
              <p className="text-muted mb-0">Total Employees</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="text-center shadow-sm h-100 border-info">
            <Card.Body>
              <i className="fas fa-hard-hat text-info mb-2" style={{ fontSize: '2rem' }}></i>
              <h3 className="fw-bold mb-0">{kpis.inspectors}</h3>
              <p className="text-muted mb-0">Inspectors</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card className="text-center shadow-sm h-100 border-warning">
            <Card.Body>
              <i className="fas fa-user-check text-warning mb-2" style={{ fontSize: '2rem' }}></i>
              <h3 className="fw-bold mb-0">{kpis.senior_inspectors}</h3>
              <p className="text-muted mb-0">Reviewers</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters + Add button */}
      <Card className="shadow-sm mb-3">
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="g-2 align-items-end">
              <Col md={4}>
                <Form.Label className="small text-muted mb-1">Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text><i className="fas fa-search"></i></InputGroup.Text>
                  <Form.Control
                    placeholder="Emp code, name, email or phone"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Label className="small text-muted mb-1">Department</Form.Label>
                <Form.Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                  <option value="">All Departments</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </Form.Select>
              </Col>
              <Col md="auto">
                <Button type="submit" variant="primary"><i className="fas fa-search me-1"></i>Search</Button>
              </Col>
              <Col md="auto">
                <Button type="button" variant="outline-secondary" onClick={handleClearFilters}>Clear</Button>
              </Col>
              <Col className="text-end">
                <Button variant="success" onClick={openAddModal}>
                  <i className="fas fa-plus me-1"></i>Add Employee
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Emp Code</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Registration Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-4">No employees found</td>
                    </tr>
                  ) : (
                    paginatedEmployees.map(emp => (
                      <tr key={emp.eid}>
                        <td><strong>{emp.emp_code}</strong></td>
                        <td>{emp.ename || <span className="text-muted fst-italic">—</span>}</td>
                        <td>{emp.email}</td>
                        <td>{emp.phone}</td>
                        <td>{emp.department}</td>
                        <td>
                          {emp.assigned_role ? (
                            <Badge bg={emp.assigned_role === 'admin' ? 'danger' : emp.assigned_role === 'sr_inspector' ? 'warning' : 'primary'} text={emp.assigned_role === 'sr_inspector' ? 'dark' : undefined}>
                              {ROLE_LABELS[emp.assigned_role] || emp.assigned_role}
                            </Badge>
                          ) : (
                            <Badge bg="secondary">Not Set</Badge>
                          )}
                        </td>
                        <td>
                          {emp.login_status === 'blocked' ? (
                            <Badge bg="danger">Blocked</Badge>
                          ) : emp.login_status === 'registered' ? (
                            <Badge bg="success">Registered</Badge>
                          ) : (
                            <Badge bg="secondary">Not Registered</Badge>
                          )}
                        </td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" className="me-2" title="Edit" onClick={() => openEditModal(emp)}>
                            <i className="fas fa-edit"></i>
                          </Button>
                          {emp.login_status === 'blocked' ? (
                            <Button size="sm" variant="outline-success" title="Activate" onClick={() => confirmBlockToggle(emp)}>
                              <i className="fas fa-user-check"></i>
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline-danger" title="Deactivate" onClick={() => confirmBlockToggle(emp)}>
                              <i className="fas fa-user-slash"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add / Edit Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Employee Code</Form.Label>
              <Form.Control
                value={formData.emp_code}
                onChange={(e) => handleFormChange('emp_code', e.target.value)}
                disabled={!!editingEmployee}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={formData.ename}
                onChange={(e) => handleFormChange('ename', e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Control
                value={formData.department}
                onChange={(e) => handleFormChange('department', e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => handleFormChange('role', e.target.value)}
                required
              >
                <option value="">Select Role</option>
                <option value="jr_inspector">Inspector</option>
                <option value="sr_inspector">Reviewer</option>
                <option value="admin">Admin</option>
              </Form.Select>
              {editingEmployee && (
                <Form.Text className="text-muted">
                  Changing this will also update the employee's login role if they're already registered.
                </Form.Text>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? <Spinner animation="border" size="sm" /> : (editingEmployee ? 'Save Changes' : 'Add Employee')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Deactivate / Activate confirmation */}
      <Modal show={!!blockTarget} onHide={() => setBlockTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{blockTarget?.login_status === 'blocked' ? 'Activate Employee' : 'Deactivate Employee'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {blockError && <Alert variant="danger">{blockError}</Alert>}
          {blockTarget && (
            blockTarget.login_status === 'blocked' ? (
              <p>
                Are you sure you want to activate <strong>{blockTarget.ename || blockTarget.emp_code}</strong> ({blockTarget.emp_code})?
                They will be able to log in (or register, if not yet registered) again.
              </p>
            ) : (
              <>
                <p>
                  Are you sure you want to deactivate <strong>{blockTarget.ename || blockTarget.emp_code}</strong> ({blockTarget.emp_code})?
                </p>
                <Alert variant="warning" className="mb-0">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {blockTarget.registered
                    ? 'This will immediately block their existing login account and prevent future logins.'
                    : 'This employee is not registered yet. They will be prevented from registering until reactivated.'}
                </Alert>
              </>
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBlockTarget(null)}>Cancel</Button>
          <Button
            variant={blockTarget?.login_status === 'blocked' ? 'success' : 'danger'}
            onClick={handleBlockToggle}
            disabled={blocking}
          >
            {blocking ? <Spinner animation="border" size="sm" /> : (blockTarget?.login_status === 'blocked' ? 'Activate' : 'Deactivate')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageEmployeesSection;
